import React, { useState, useEffect } from 'react';
import {
  useLocationsStats, usePassesStats, useSavedStats, useSanaStats,
  useRevenueStats, useCommunityStats, useHealthProfileStats, useSearchStats,
  usePipelineStats, useActivityFeed, useCommandGeo, useEngagementSeries,
} from '../hooks/useAnalytics';
import { supabase } from '../supabaseClient';
import { setFocus, takeFocus } from '../utils/consoleBus';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const SANOIRE_PAPER = 'linear-gradient(180deg, #E8EEF2 0%, #F2EEE7 50%, #EDE8E0 100%)';
const SERIF = 'Georgia,"Times New Roman",serif';

/* ── Themes ──────────────────────────────────────────────────────────────── */
const INK_PAPER = {
  bg:'#F2EEE7', panel:'#ffffff', top:'#ffffff', kbg:'#ffffff', bd:'#e4ded2',
  tx:'#15242e', mut:'#6a8494', seal:'#314F5C', sealc:'#ffffff', period:'#f5f1e8',
  gbar:'#EDE8E0', accent:'#314F5C', up:'#17a86a', search:'#2563eb', warn:'#d97706',
  danger:'#dc3545', alert:'#fdf4e3', alertc:'#7c4a0a', alertbd:'#ecd9a8', pdon:'#efe7d8',
  codebg:'#f1ede3', live:'#eaf7f2', livec:'#0d6640', rowbg:'#faf8f2',
  gold:'#9F7E4F', goldSoft:'#B99C6D', ink:'#112433', inkDeep:'#0d1d2b', inkTx:'#F2EEE7', inkMut:'#7fa0b4',
};
const THEMES = { dark: INK_PAPER, light: INK_PAPER };

function fmt(n) {
  if (n === null || n === undefined) return '—';
  if (n >= 1e6) return (n/1e6).toFixed(2).replace(/\.?0+$/,'') + 'M';
  if (n >= 1e3) return n.toLocaleString();
  return String(n);
}
function fmtUSD(n) {
  if (n === null || n === undefined) return '—';
  return '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function pct(a, b) { return b ? Math.round((a / b) * 100) : 0; }

function useIsMobile() {
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.innerWidth < 820);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 820);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

/* ── Building blocks ─────────────────────────────────────────────────────── */
function SectionLabel({ t, children, right }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, margin:'18px 2px 10px' }}>
      <span style={{ fontSize:'0.88rem', fontWeight:600, color:t.mut, textTransform:'uppercase', letterSpacing:'.12em', whiteSpace:'nowrap' }}>{children}</span>
      <div style={{ flex:1, height:1, background:t.bd }} />
      {right && <span style={{ fontSize:'0.88rem', color:t.mut }}>{right}</span>}
    </div>
  );
}

function KPI({ t, label, value, sub, subColor, accent, delta, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div onClick={onClick}
      onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}
      style={{ background:t.ink, border:'1px solid rgba(185,156,109,0.22)', borderRadius:10, padding:'13px 15px',
        position:'relative', overflow:'hidden', cursor:onClick?'pointer':'default', boxShadow:'0 2px 8px rgba(13,29,43,0.18)',
        transform: hover && onClick ? 'translateY(-2px)' : 'none', transition:'transform .15s' }}>
      {accent && <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:accent }} />}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:6, marginBottom:7 }}>
        <span style={{ fontSize:'0.76rem', fontWeight:600, color:t.inkMut, letterSpacing:'.13em', textTransform:'uppercase' }}>{label}</span>
        {delta != null && !isNaN(delta) && <span style={{ fontSize:'0.82rem', fontWeight:600, color: delta > 0 ? '#5dcaa5' : delta < 0 ? '#e8736b' : t.inkMut, whiteSpace:'nowrap' }}>{delta > 0 ? '▲' : delta < 0 ? '▼' : '·'} {Math.abs(Math.round(delta))}%</span>}
      </div>
      <div style={{ fontSize:'2rem', fontWeight:500, color:t.inkTx, lineHeight:1.05, fontFamily:SERIF, fontVariantNumeric:'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize:'0.92rem', fontFamily:SERIF, fontStyle:'italic', color:subColor||t.goldSoft, marginTop:5 }}>{sub}</div>}
    </div>
  );
}

function Panel({ t, title, right, children, style }) {
  return (
    <div style={{ background:t.panel, border:`1px solid ${t.bd}`, borderRadius:11, padding:'14px 16px', ...style }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:13 }}>
        <span style={{ fontSize:'1.1rem', fontWeight:500, color:t.tx, letterSpacing:'.01em' }}>{title}</span>
        {right && <span style={{ fontSize:'0.95rem', color:t.mut }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

function StatLine({ t, label, value, color }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'1.05rem', padding:'4px 0' }}>
      <span style={{ color:t.mut }}>{label}</span>
      <b style={{ color:color||t.tx, fontVariantNumeric:'tabular-nums', fontWeight:500 }}>{value}</b>
    </div>
  );
}

function GeoRow({ t, name, n, photo, max, onSelect }) {
  const w = max ? Math.round(n/max*100) : 0;
  const pw = max ? Math.round(photo/max*100) : 0;
  const p = n ? Math.round(photo/n*100) : 0;
  const [h, setH] = useState(false);
  return (
    <div onClick={() => onSelect && onSelect(name)} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ display:'flex', alignItems:'center', gap:9, marginBottom:8, fontSize:'1.02rem', position:'relative', cursor:onSelect?'pointer':'default', background:h?t.rowbg:'transparent', borderRadius:6 }}>
      {h && <div style={{ position:'absolute', right:70, bottom:'100%', marginBottom:4, background:t.codebg, color:t.tx, fontSize:'0.85rem', padding:'5px 9px', borderRadius:6, whiteSpace:'nowrap', zIndex:20, border:`1px solid ${t.bd}`, boxShadow:'0 6px 16px rgba(0,0,0,.3)' }}>{name}: {Number(n).toLocaleString()} records · {p}% photos — click to inspect</div>}
      <span style={{ width:104, flexShrink:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:t.tx }}>{name}</span>
      <div style={{ flex:1, height:14, borderRadius:4, position:'relative', overflow:'hidden', background:t.gbar }}>
        <div style={{ position:'absolute', left:0, top:0, bottom:0, borderRadius:4, width:`${w}%`, background:t.accent, opacity:.32 }} />
        <div style={{ position:'absolute', left:0, top:0, bottom:0, borderRadius:4, width:`${pw}%`, background:t.up }} />
      </div>
      <span style={{ width:52, textAlign:'right', flexShrink:0, fontVariantNumeric:'tabular-nums', color:t.mut }}>{fmt(n)}</span>
      <span style={{ width:38, textAlign:'right', flexShrink:0, fontVariantNumeric:'tabular-nums', color: p < 5 ? t.warn : t.mut }}>{p}%</span>
    </div>
  );
}

function TypeRow({ t, type, count, total, max, onSelect }) {
  const w = max ? Math.round(count/max*100) : 0;
  const [h, setH] = useState(false);
  return (
    <div onClick={() => onSelect && onSelect(type)} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ display:'flex', alignItems:'center', gap:9, marginBottom:8, fontSize:'1.02rem', position:'relative', cursor:onSelect?'pointer':'default', background:h?t.rowbg:'transparent', borderRadius:6 }}>
      {h && <div style={{ position:'absolute', right:90, bottom:'100%', marginBottom:4, background:t.codebg, color:t.tx, fontSize:'0.85rem', padding:'5px 9px', borderRadius:6, whiteSpace:'nowrap', zIndex:20, border:`1px solid ${t.bd}`, boxShadow:'0 6px 16px rgba(0,0,0,.3)' }}>{type}: {Number(count).toLocaleString()} venues — click to inspect</div>}
      <span style={{ width:120, flexShrink:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:t.tx }}>{type}</span>
      <div style={{ flex:1, height:14, borderRadius:4, overflow:'hidden', background:t.gbar }}>
        <div style={{ height:'100%', width:`${w}%`, borderRadius:4, background:t.accent, opacity:.5 }} />
      </div>
      <span style={{ width:30, textAlign:'right', flexShrink:0, color:t.mut }}>{pct(count,total)}%</span>
      <span style={{ width:52, textAlign:'right', flexShrink:0, fontVariantNumeric:'tabular-nums', color:t.mut }}>{fmt(count)}</span>
    </div>
  );
}

function Ring({ t, pct: p, label }) {
  const r = 32, c = 2*Math.PI*r;
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" style={{ flexShrink:0 }}>
      <circle cx="40" cy="40" r={r} fill="none" stroke={t.gbar} strokeWidth="8" />
      <circle cx="40" cy="40" r={r} fill="none" stroke={t.up} strokeWidth="8" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c*(1-p/100)} transform="rotate(-90 40 40)" />
      <text x="40" y="37" textAnchor="middle" fontSize="17" fontWeight="500" fill={t.tx} fontFamily={F}>{p}%</text>
      <text x="40" y="51" textAnchor="middle" fontSize="8.5" fill={t.mut} fontFamily={F}>{label}</text>
    </svg>
  );
}

function Sparkline({ t, days }) {
  if (!days || days.length < 2) {
    return <div style={{ height:96, display:'flex', alignItems:'center', justifyContent:'center', color:t.mut, fontSize:'1.02rem' }}>Awaiting signal</div>;
  }
  const W = 520, H = 96, PAD = 8;
  const maxV = Math.max(1, ...days.map(d => Math.max(d.sana, d.search)));
  const x = i => (i/(days.length-1)) * W;
  const y = v => H - PAD - (v/maxV) * (H - PAD*2);
  const line = key => days.map((d,i) => `${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`).join(' ');
  return <SparklineSVG t={t} days={days} W={W} H={H} x={x} y={y} line={line} />;
}

function SparklineSVG({ t, days, W, H, x, y, line }) {
  const [hi, setHi] = useState(null);
  const sel = hi != null ? days[hi] : null;
  return (
    <div style={{ position:'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', height:96 }} preserveAspectRatio="none"
        onMouseLeave={() => setHi(null)}
        onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); const fx = (e.clientX - r.left) / r.width * W; setHi(Math.max(0, Math.min(days.length - 1, Math.round(fx / W * (days.length - 1))))); }}>
        <polyline points={line('search')} style={{ fill:'none', stroke:t.search, strokeWidth:2 }} />
        <polyline points={line('sana')} style={{ fill:'none', stroke:t.up, strokeWidth:2 }} />
        {sel && <line x1={x(hi)} y1={0} x2={x(hi)} y2={H} stroke={t.bd} strokeWidth={1} />}
        {sel && <circle cx={x(hi)} cy={y(sel.search)} r={3.5} fill={t.search} />}
        {sel && <circle cx={x(hi)} cy={y(sel.sana)} r={3.5} fill={t.up} />}
      </svg>
      {sel && (
        <div style={{ position:'absolute', top:0, left:`${(hi/(days.length-1))*100}%`, transform:'translateX(-50%)', background:t.codebg, color:t.tx, fontSize:'0.82rem', padding:'4px 8px', borderRadius:6, whiteSpace:'nowrap', border:`1px solid ${t.bd}`, pointerEvents:'none' }}>
          {sel.label}: Sana {sel.sana} · Search {sel.search}
        </div>
      )}
    </div>
  );
}

function AlertRow({ t, color, title, desc, action, onAction }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:13, padding:'11px 13px', background:t.rowbg, borderRadius:9, marginBottom:6, border:`1px solid ${t.bd}` }}>
      <div style={{ width:8, height:8, borderRadius:'50%', background:color, flexShrink:0 }} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:'1.05rem', fontWeight:500, color:t.tx }}>{title}</div>
        {desc && <div style={{ fontSize:'0.9rem', color:t.mut, marginTop:2 }}>{desc}</div>}
      </div>
      {action && (
        <button onClick={onAction} style={{ fontSize:'0.9rem', fontWeight:600, color:t.tx, background:t.period, border:`1px solid ${t.bd}`, borderRadius:6, padding:'5px 11px', cursor:'pointer', whiteSpace:'nowrap', fontFamily:F }}>
          {action} →
        </button>
      )}
    </div>
  );
}

function FeatureCard({ t, title, accent, big, bigLabel, rows, navId, navLabel, onNav }) {
  return (
    <div style={{ background:t.panel, border:`1px solid ${t.bd}`, borderRadius:11, padding:'14px 16px', position:'relative', overflow:'hidden' }}>
      {accent && <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:accent }} />}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <span style={{ fontSize:'1.05rem', color:t.tx, fontWeight:500 }}>{title}</span>
        {navId && (
          <button onClick={() => onNav && onNav(navId)} style={{ fontSize:'0.85rem', fontWeight:600, color:t.mut, background:'transparent', border:`1px solid ${t.bd}`, borderRadius:6, padding:'3px 9px', cursor:'pointer', fontFamily:F }}>
            {navLabel || 'View'} →
          </button>
        )}
      </div>
      <div style={{ fontSize:'2.31rem', fontWeight:500, color:t.tx, lineHeight:1, letterSpacing:'-.02em', marginBottom:2, fontVariantNumeric:'tabular-nums' }}>{big}</div>
      <div style={{ fontSize:'0.93rem', color:t.mut, marginBottom:12 }}>{bigLabel}</div>
      {rows.map((r, i) => (
        <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderTop:`1px solid ${t.bd}` }}>
          <span style={{ fontSize:'1rem', color:t.mut, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'62%' }}>{r.label}</span>
          <span style={{ fontSize:'1rem', fontWeight:500, color:r.color||t.tx, fontVariantNumeric:'tabular-nums' }}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

function FeedItem({ t, item }) {
  const color =
    item.action_type === 'pass_purchased'     ? '#17a86a'
    : item.action_type === 'waitlist_invited' ? t.accent
    : item.action_type === 'support_reply'    ? t.search
    : item.action_type === 'admin_login'      ? t.mut
    : t.up;
  const ts = item.created_at
    ? new Date(item.created_at).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' })
    : '';
  return (
    <div style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'9px 0', borderBottom:`1px solid ${t.bd}` }}>
      <div style={{ width:8, height:8, borderRadius:'50%', background:color, flexShrink:0, marginTop:4 }} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:'1.02rem', color:t.tx, fontWeight:500 }}>{item.subject || (item.action_type || '').replace(/_/g,' ')}</div>
        {item.detail && <div style={{ fontSize:'0.79rem', color:t.mut, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.detail}</div>}
      </div>
      <span style={{ fontSize:'0.85rem', color:t.mut, flexShrink:0, fontVariantNumeric:'tabular-nums' }}>{ts}</span>
    </div>
  );
}

/* ── Screen ──────────────────────────────────────────────────────────────── */
function CCCountryInspector({ t, country }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let c = false; setD(null);
    supabase.rpc('admin_country_detail', { p_country: country }).then(({ data }) => { if (!c) setD(data || {}); });
    return () => { c = true; };
  }, [country]);
  if (!d) return <div style={{ color:t.mut, fontSize:'0.95rem' }}>Loading {country}…</div>;
  const photoPct = d.n ? Math.round(d.with_photo/d.n*100) : 0;
  const floorPct = d.n ? Math.round(d.floor_ok/d.n*100) : 0;
  const types = d.top_types || []; const maxT = Math.max(1, ...types.map(x => x.n));
  const cities = d.top_cities || []; const maxC = Math.max(1, ...cities.map(x => x.n));
  const row = (label, val, color) => (
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.95rem', padding:'5px 0' }}>
      <span style={{ color:t.mut }}>{label}</span><b style={{ color:color||t.tx, fontWeight:500 }}>{val}</b>
    </div>
  );
  const bar = (label, val, pct, color) => (
    <div key={label} style={{ display:'flex', alignItems:'center', gap:9, marginBottom:7, fontSize:'0.9rem' }}>
      <span style={{ width:120, flexShrink:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:t.tx }}>{label}</span>
      <div style={{ flex:1, height:14, borderRadius:4, overflow:'hidden', background:t.gbar }}>
        <div style={{ height:'100%', width:`${pct}%`, borderRadius:4, background:color||t.accent, opacity:color?1:.5 }} />
      </div>
      <span style={{ minWidth:54, textAlign:'right', color:t.mut, fontVariantNumeric:'tabular-nums' }}>{fmt(val)}</span>
    </div>
  );
  return (
    <div>
      <div style={{ fontSize:'1.8rem', fontWeight:500, color:t.tx }}>{fmt(d.n)}</div>
      <div style={{ fontSize:'0.85rem', color:t.mut, marginBottom:12 }}>records · {fmt(d.cities)} cities</div>
      {row('Display-floor pass', `${floorPct}%`)}
      {row('Has photo', `${photoPct}%`, photoPct < 5 ? t.warn : t.tx)}
      {row('Has phone', d.n ? `${Math.round(d.with_phone/d.n*100)}%` : '—')}
      <div style={{ height:1, background:t.bd, margin:'12px 0' }} />
      <div style={{ fontSize:'0.8rem', color:t.mut, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>Top types</div>
      {types.length === 0 ? <div style={{ color:t.mut, fontSize:'0.9rem' }}>No type data.</div> : types.map(x => bar(x.type, x.n, Math.round(x.n/maxT*100)))}
      <div style={{ fontSize:'0.8rem', color:t.mut, textTransform:'uppercase', letterSpacing:'.06em', margin:'12px 0 8px' }}>Top cities</div>
      {cities.length === 0 ? <div style={{ color:t.mut, fontSize:'0.9rem' }}>No city data.</div> : cities.map(x => bar(x.city, x.n, Math.round(x.n/maxC*100), t.accent))}
    </div>
  );
}

function CCTypeInspector({ t, type }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let c = false; setD(null);
    supabase.rpc('admin_type_detail', { p_type: type }).then(({ data }) => { if (!c) setD(data || {}); });
    return () => { c = true; };
  }, [type]);
  if (!d) return <div style={{ color:t.mut, fontSize:'0.95rem' }}>Loading {type}…</div>;
  const photoPct = d.n ? Math.round(d.with_photo/d.n*100) : 0;
  const floorPct = d.n ? Math.round(d.floor_ok/d.n*100) : 0;
  const countries = d.top_countries || []; const maxC = Math.max(1, ...countries.map(x => x.n));
  const cities = d.top_cities || []; const maxCi = Math.max(1, ...cities.map(x => x.n));
  const row = (label, val, color) => (
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.95rem', padding:'5px 0' }}>
      <span style={{ color:t.mut }}>{label}</span><b style={{ color:color||t.tx, fontWeight:500 }}>{val}</b>
    </div>
  );
  const bar = (label, val, pct, color) => (
    <div key={label} style={{ display:'flex', alignItems:'center', gap:9, marginBottom:7, fontSize:'0.9rem' }}>
      <span style={{ width:120, flexShrink:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:t.tx }}>{label}</span>
      <div style={{ flex:1, height:14, borderRadius:4, overflow:'hidden', background:t.gbar }}>
        <div style={{ height:'100%', width:`${pct}%`, borderRadius:4, background:color||t.accent, opacity:color?1:.5 }} />
      </div>
      <span style={{ minWidth:54, textAlign:'right', color:t.mut, fontVariantNumeric:'tabular-nums' }}>{fmt(val)}</span>
    </div>
  );
  return (
    <div>
      <div style={{ fontSize:'1.8rem', fontWeight:500, color:t.tx }}>{fmt(d.n)}</div>
      <div style={{ fontSize:'0.85rem', color:t.mut, marginBottom:12 }}>venues · {fmt(d.countries)} countries</div>
      {row('Display-floor pass', `${floorPct}%`)}
      {row('Has photo', `${photoPct}%`, photoPct < 5 ? t.warn : t.tx)}
      <div style={{ height:1, background:t.bd, margin:'12px 0' }} />
      <div style={{ fontSize:'0.8rem', color:t.mut, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>Top countries</div>
      {countries.length === 0 ? <div style={{ color:t.mut, fontSize:'0.9rem' }}>No data.</div> : countries.map(x => bar(x.country, x.n, Math.round(x.n/maxC*100)))}
      <div style={{ fontSize:'0.8rem', color:t.mut, textTransform:'uppercase', letterSpacing:'.06em', margin:'12px 0 8px' }}>Top cities</div>
      {cities.length === 0 ? <div style={{ color:t.mut, fontSize:'0.9rem' }}>No data.</div> : cities.map(x => bar(x.city, x.n, Math.round(x.n/maxCi*100), t.accent))}
    </div>
  );
}

function GrowthChart({ t, days }) {
  const [hi, setHi] = useState(null);
  if (!days || days.length < 2) return <div style={{ height:140, display:'flex', alignItems:'center', justifyContent:'center', color:t.mut, fontSize:'1.02rem' }}>Awaiting data…</div>;
  const W = 900, H = 150, PAD = 10;
  const series = [
    { key:'signups', color:t.warn, label:'Signups' },
    { key:'sana', color:t.up, label:'Sana' },
    { key:'search', color:t.search, label:'Search' },
  ];
  const maxV = Math.max(1, ...days.map(d => Math.max(d.signups, d.sana, d.search)));
  const xf = i => PAD + (i/(days.length-1)) * (W - 2*PAD);
  const yf = v => H - PAD - (v/maxV) * (H - 2*PAD);
  const line = key => days.map((d,i) => `${xf(i).toFixed(1)},${yf(d[key]).toFixed(1)}`).join(' ');
  const sel = hi != null ? days[hi] : null;
  return (
    <div>
      <div style={{ position:'relative' }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', height:150 }} preserveAspectRatio="none"
          onMouseLeave={() => setHi(null)}
          onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); const fx = (e.clientX - r.left) / r.width * W; setHi(Math.max(0, Math.min(days.length-1, Math.round((fx - PAD) / (W - 2*PAD) * (days.length-1))))); }}>
          {series.map(sr => <polyline key={sr.key} points={line(sr.key)} style={{ fill:'none', stroke:sr.color, strokeWidth:2 }} />)}
          {sel && <line x1={xf(hi)} y1={0} x2={xf(hi)} y2={H} stroke={t.bd} strokeWidth={1} />}
          {sel && series.map(sr => <circle key={sr.key} cx={xf(hi)} cy={yf(sel[sr.key])} r={3} fill={sr.color} />)}
        </svg>
        {sel && <div style={{ position:'absolute', top:0, left:`${(hi/(days.length-1))*100}%`, transform:'translateX(-50%)', background:t.codebg, color:t.tx, fontSize:'0.8rem', padding:'5px 9px', borderRadius:6, whiteSpace:'nowrap', border:`1px solid ${t.bd}`, pointerEvents:'none' }}>{sel.day}: {sel.signups} signups · {sel.sana} Sana · {sel.search} search</div>}
      </div>
      <div style={{ display:'flex', gap:16, marginTop:8, fontSize:'0.85rem', color:t.mut, flexWrap:'wrap' }}>
        {series.map(sr => <span key={sr.key}><span style={{ width:10, height:10, borderRadius:2, display:'inline-block', marginRight:5, background:sr.color, verticalAlign:'middle' }} />{sr.label}</span>)}
      </div>
    </div>
  );
}

function useGrowthSeries() {
  const [d, setD] = useState(null);
  useEffect(() => { let c = false; supabase.rpc('admin_growth_series').then(({ data }) => { if (!c) setD(data || []); }); return () => { c = true; }; }, []);
  return d;
}

function CCDrawer({ t, open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:9000, background:'rgba(5,12,20,0.5)', display:'flex', justifyContent:'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width:420, maxWidth:'94vw', height:'100%', overflowY:'auto', background:t.panel, borderLeft:`1px solid ${t.bd}`, fontFamily:F, boxShadow:'-8px 0 28px rgba(0,0,0,.3)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 18px', borderBottom:`1px solid ${t.bd}`, background:t.top, position:'sticky', top:0 }}>
          <span style={{ fontSize:'1rem', fontWeight:500, color:t.tx }}>{title}</span>
          <button onClick={onClose} style={{ width:28, height:28, borderRadius:6, border:`1px solid ${t.bd}`, background:t.period, color:t.tx, cursor:'pointer' }}>✕</button>
        </div>
        <div style={{ padding:18 }}>{children}</div>
      </div>
    </div>
  );
}

export default function CommandCenter({ onNav, period }) {
  const mobile = useIsMobile();
  // Single committed Ink & Paper theme — no light/dark toggle.
  const t = THEMES.light;

  const [sel, setSel] = useState(null);
  const [selType, setSelType] = useState(null);
  useEffect(() => { const f = takeFocus(['country', 'type']); if (f) { if (f.type === 'country') setSel(f.value); else setSelType(f.value); } }, []);

  const { data: locs, loading: lL } = useLocationsStats();
  const passes = usePassesStats().data;
  const saved = useSavedStats().data;
  const { data: sana } = useSanaStats();
  const { data: rev } = useRevenueStats();
  const { data: comm } = useCommunityStats();
  const { data: prof } = useHealthProfileStats();
  const { data: search } = useSearchStats();
  const { data: pipe } = usePipelineStats();
  const { events: feed, loading: afL } = useActivityFeed(12);
  const { data: geo, loading: geoLoading, refresh: refreshGeo } = useCommandGeo();
  const { data: eng } = useEngagementSeries();
  const growth = useGrowthSeries();
  const gser = growth || [];
  const pWin = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 60 : gser.length;
  const sumWin = (key, n) => gser.slice(-n).reduce((a, dd) => a + (dd[key] || 0), 0);
  const sumPrev = (key, n) => gser.slice(Math.max(0, gser.length - 2 * n), gser.length - n).reduce((a, dd) => a + (dd[key] || 0), 0);
  const sanaWin = sumWin('sana', pWin);
  const sanaPrevWin = sumPrev('sana', pWin);
  const sanaDelta = (period !== 'All' && sanaPrevWin > 0) ? Math.round((sanaWin - sanaPrevWin) / sanaPrevWin * 100) : null;

  const [openTickets, setOpenTickets] = useState(null);
  const [expiringPasses, setExpiringPasses] = useState(null);
  const [dq, setDq] = useState(null);
  const [gpsCoords, setGpsCoords] = useState(null);
  const [topTypes, setTopTypes] = useState(null);
  const [alertsReady, setAlertsReady] = useState(false);
  const [abuse, setAbuse] = useState(null);
  const [cockpit, setCockpit] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function loadExtras() {
      try {
        const [{ data: ticketCount }, { data: dqs }] = await Promise.all([
          supabase.rpc('admin_open_ticket_count'),
          supabase.rpc('get_data_quality_stats'),
        ]);
        const nowIso = new Date().toISOString();
        const in7 = new Date(Date.now() + 7 * 86400000).toISOString();
        const startMonthIso = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
        const weekAgoIso = new Date(Date.now() - 7 * 86400000).toISOString();
        const [
          { count: expiring }, { count: gCoords }, { count: flaggedCount },
          { count: lockedCount }, { count: refundsThisMonth }, { data: cancelWeekRows },
        ] = await Promise.all([
          supabase.from('user_passes').select('*', { count:'exact', head:true }).gt('expires_at', nowIso).lt('expires_at', in7),
          supabase.from('Locations').select('*', { count:'exact', head:true }).not('latitude','is',null).neq('latitude',0),
          supabase.from('user_abuse_signals').select('*', { count:'exact', head:true }).eq('flagged', true),
          supabase.from('user_suspensions').select('*', { count:'exact', head:true }).gt('locked_until', nowIso),
          supabase.from('user_passes').select('*', { count:'exact', head:true }).gte('refunded_at', startMonthIso),
          supabase.from('user_passes').select('id,created_at,cancelled_at').not('cancelled_at','is',null).gte('cancelled_at', weekAgoIso).limit(500),
        ]);
        const sameDayCancels = (cancelWeekRows || []).filter(r =>
          r.created_at && r.cancelled_at && (new Date(r.cancelled_at) - new Date(r.created_at)) < 86400000).length;
        if (!cancelled) {
          setOpenTickets(typeof ticketCount === 'number' ? ticketCount : 0);
          setDq(dqs);
          setExpiringPasses(expiring || 0);
          setGpsCoords(gCoords || 0);
          setAbuse({ flagged: flaggedCount||0, locked: lockedCount||0, sameDayCancelsWeek: sameDayCancels, refundsThisMonth: refundsThisMonth||0 });
          setAlertsReady(true);
        }
      } catch (_e) { if (!cancelled) setAlertsReady(true); }
    }
    loadExtras();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadTypes() {
      if (locs?.topTypes?.length > 0) { setTopTypes(locs.topTypes); return; }
      try {
        const res = await fetch('/api/directory-stats');
        if (res.ok) { const d = await res.json(); if (!cancelled && d.topTypes?.length) setTopTypes(d.topTypes); }
      } catch (_e) {}
    }
    loadTypes();
    return () => { cancelled = true; };
  }, [locs]);

  useEffect(() => { let c = false; supabase.rpc('admin_cockpit_rollup').then(({ data }) => { if (!c) setCockpit(data || {}); }); return () => { c = true; }; }, []);

  const totalLocs = (geo && geo.total) || locs?.total || dq?.total || 0;
  const floorPct = geo && geo.total ? Math.round(geo.floor_ok/geo.total*100) : 0;
  const photoPct = geo && geo.total ? Math.round(geo.with_photo/geo.total*100) : 0;
  const phonePct = geo && geo.total ? Math.round(geo.with_phone/geo.total*100) : 0;
  const gpsCov = (totalLocs && gpsCoords != null) ? Math.round((gpsCoords/totalLocs)*100) : null;
  const webCov = dq ? Math.round((dq.has_website/dq.total)*100) : null;
  const verCov = dq ? Math.round((dq.verify_done/dq.total)*100) : null;
  const belowPhoto = geo ? (geo.total - geo.with_photo) : 0;
  const hasRevenue = rev && rev.totalRev > 0;
  const top = (geo && geo.top) || [];
  const maxN = top.length ? top[0].n : 0;
  const maxT = topTypes && topTypes.length ? topTypes[0].count : 0;

  const alerts = [];
  if (alertsReady) {
    if (openTickets > 0) alerts.push({ color:'#e53935', title:`${openTickets} open support ticket${openTickets!==1?'s':''}`, desc:'Unresolved customer requests', action:'Help', nav:'customerhelp' });
    if (pipe?.failed > 0) alerts.push({ color:'#e53935', title:`${pipe.failed} pipeline run${pipe.failed!==1?'s':''} failed`, desc:'Check logs for error details', action:'Pipeline', nav:'pipeline' });
    if (expiringPasses > 0) alerts.push({ color:'#d97706', title:`${fmt(expiringPasses)} passes expire in 7 days`, desc:'Consider a renewal email', action:'Revenue', nav:'revenue' });
    if (dq?.missing_coords > 0) alerts.push({ color:'#d97706', title:`${fmt(dq.missing_coords)} locations missing GPS`, desc:`${gpsCov||0}% coverage — affects near-me search`, action:'Data', nav:'dataquality' });
    if (dq?.flagged_count > 0) alerts.push({ color:'#d97706', title:`${fmt(dq.flagged_count)} flagged records`, desc:'Type/tag issues from verify', action:'Data', nav:'dataquality' });
    if (!lL && (locs?.never || 0) > 5000) alerts.push({ color:t.mut, title:`${fmt(locs.never)} never verified`, desc:`${pct(locs.never, totalLocs)}% of database`, action:'Data', nav:'data' });
  }

  const col2 = mobile ? '1fr' : '1.35fr 1fr';
  const col2b = mobile ? '1fr' : '1fr 1fr';
  const col3 = mobile ? '1fr' : 'repeat(3,1fr)';
  const col4 = mobile ? '1fr 1fr' : 'repeat(4,1fr)';
  const cm = (cockpit && cockpit.manage) || {};
  const ck = (cockpit && cockpit.marketing) || {};
  const ct = (cockpit && cockpit.team) || {};

  return (
    <div style={{ background: SANOIRE_PAPER, borderRadius:0, overflow:'hidden', fontFamily:F, width:'100%', minHeight:'100%' }}>


      <div style={{ padding:16 }}>

        {/* KPI row */}
        <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(5,1fr)', gap:10 }}>
          <KPI t={t} label={`Net revenue · ${period === '7d' ? '7d' : period === 'All' ? 'all-time' : '30d'}`}
            value={hasRevenue ? fmtUSD(period === '7d' ? rev.rev7 : period === 'All' ? rev.totalRev : rev.rev30) : '$0'}
            sub={hasRevenue ? `${fmtUSD(rev.totalRev)} all-time` : 'Awaiting launch'}
            subColor={hasRevenue ? t.up : t.mut} accent="#17a86a" onClick={() => onNav && onNav('revenue')} />
          <KPI t={t} label="Pass holders"
            value={fmt(passes.uniqueUsers)} sub={`${fmt(passes.active)} active`}
            accent="#819D84" onClick={() => onNav && onNav('revenue')} />
          <KPI t={t} label={period === 'All' ? 'Sana queries' : `Sana queries · ${period}`}
            value={period === 'All' ? fmt(sana.total) : fmt(sanaWin)} delta={sanaDelta}
            sub={period === 'All' ? `+${fmt(search.total)} searches` : 'vs prior period'} subColor={t.up}
            accent="#5a8fa6" onClick={() => onNav && onNav('sana')} />
          <KPI t={t} label="Directory records"
            value={geo ? fmt(geo.total) : (lL ? '…' : fmt(totalLocs))}
            sub={geo ? `${geo.countries} countries` : '…'} subColor={t.up}
            accent={t.accent} onClick={() => onNav && onNav('locations')} />
          <KPI t={t} label="Open tickets"
            value={alertsReady ? fmt(openTickets ?? 0) : '…'}
            sub={openTickets > 0 ? 'needs attention' : 'all clear'}
            subColor={openTickets > 0 ? t.warn : t.up}
            accent={openTickets > 0 ? '#e53935' : '#17a86a'} onClick={() => onNav && onNav('customerhelp')} />
        </div>

        {/* Needs attention */}
        {alerts.length > 0 && (
          <>
            <SectionLabel t={t}>Needs attention ({alerts.length})</SectionLabel>
            <div>{alerts.map((a, i) => (
              <AlertRow key={i} t={t} color={a.color} title={a.title} desc={a.desc} action={a.action} onAction={() => onNav && onNav(a.nav)} />
            ))}</div>
          </>
        )}

        {/* Geographic + Directory health */}
        <SectionLabel t={t}>Directory intelligence</SectionLabel>
        <div style={{ display:'grid', gridTemplateColumns:col2, gap:12, marginBottom:12 }}>
          <Panel t={t} title="◷ Geographic distribution" right="records · photo coverage">
            {geoLoading && !top.length
              ? <div style={{ color:t.mut, fontSize:'1.05rem', padding:'12px 0' }}>Loading geography…</div>
              : top.map(g => <GeoRow key={g.country} t={t} name={g.country} n={g.n} photo={g.with_photo} max={maxN} onSelect={setSel} />)}
            <div style={{ display:'flex', gap:14, marginTop:10, fontSize:'0.95rem', color:t.mut }}>
              <span><span style={{ width:9, height:9, borderRadius:2, display:'inline-block', marginRight:4, background:t.accent, opacity:.4, verticalAlign:'middle' }} />Total records</span>
              <span><span style={{ width:9, height:9, borderRadius:2, display:'inline-block', marginRight:4, background:t.up, verticalAlign:'middle' }} />With photo</span>
            </div>
          </Panel>

          <Panel t={t} title="◈ Directory health" right={<span onClick={refreshGeo} style={{ cursor:'pointer' }}>refresh</span>}>
            <div style={{ display:'flex', gap:16, alignItems:'center', marginBottom:12 }}>
              <Ring t={t} pct={floorPct} label="floor" />
              <div style={{ flex:1 }}>
                <StatLine t={t} label="Display-floor pass" value={`${floorPct}%`} />
                <StatLine t={t} label="Has phone" value={`${phonePct}%`} />
                <StatLine t={t} label="Has photo" value={`${photoPct}%`} color={t.warn} />
                <StatLine t={t} label="GPS coords" value={gpsCov != null ? `${gpsCov}%` : '…'} />
              </div>
              <div style={{ flex:1 }}>
                <StatLine t={t} label="Website" value={webCov != null ? `${webCov}%` : '…'} />
                <StatLine t={t} label="Verified" value={verCov != null ? `${verCov}%` : '…'} />
                <StatLine t={t} label="Countries" value={geo ? fmt(geo.countries) : '…'} />
                <StatLine t={t} label="Cities · types" value={geo ? `${fmt(geo.cities)} · ${geo.types}` : '…'} />
              </div>
            </div>
            <div style={{ fontSize:'1rem', borderRadius:7, padding:'8px 10px', lineHeight:1.5, background:t.alert, color:t.alertc, border:`1px solid ${t.alertbd}` }}>
              ⚠ {fmt(belowPhoto)} records below photo tier{geo && geo.zero_photo ? ` — ${geo.zero_photo} countries at 0% coverage` : ''}
            </div>
          </Panel>
        </div>

        {/* Location types + Engagement */}
        <div style={{ display:'grid', gridTemplateColumns:col2b, gap:12 }}>
          <Panel t={t} title="◔ Location types" right={`${fmt(totalLocs)} total`}>
            {!topTypes
              ? <div style={{ color:t.mut, fontSize:'1.05rem', padding:'12px 0' }}>Loading types…</div>
              : topTypes.slice(0,8).map(ty => <TypeRow key={ty.type} t={t} type={ty.type} count={ty.count} total={totalLocs} max={maxT} onSelect={setSelType} />)}
          </Panel>
          <Panel t={t} title="◠ Engagement signal" right={period === '7d' ? 'last 7d' : 'last 14d'}>
            <Sparkline t={t} days={period === '7d' ? (eng.days || []).slice(-7) : eng.days} />
            <div style={{ display:'flex', gap:14, marginTop:8, fontSize:'0.95rem', color:t.mut, flexWrap:'wrap' }}>
              <span><span style={{ width:9, height:9, borderRadius:2, display:'inline-block', marginRight:4, background:t.up, verticalAlign:'middle' }} />Sana {fmt(eng.totalSana)}</span>
              <span><span style={{ width:9, height:9, borderRadius:2, display:'inline-block', marginRight:4, background:t.search, verticalAlign:'middle' }} />Search {fmt(eng.totalSearch)}</span>
              <span style={{ marginLeft:'auto' }}>{fmt(saved.total)} saved · {sana.resultRate}% result rate</span>
            </div>
          </Panel>
        </div>

        <SectionLabel t={t} right="last 30d">Growth trend</SectionLabel>
        <Panel t={t} title="◠ Signups · Sana · Search">
          <GrowthChart t={t} days={period === 'All' ? growth : (growth || []).slice(-pWin)} />
        </Panel>

        {/* Abuse signals */}
        {abuse && (
          <>
            <SectionLabel t={t}>Abuse signals</SectionLabel>
            <div style={{ display:'grid', gridTemplateColumns:col4, gap:10 }}>
              <KPI t={t} label="Flagged accounts" value={fmt(abuse.flagged)}
                sub={abuse.flagged > 0 ? 'review in Users' : 'all clear'} subColor={abuse.flagged>0?t.warn:t.up}
                accent={abuse.flagged > 0 ? '#dc3545' : '#17a86a'} onClick={() => onNav && onNav('users')} />
              <KPI t={t} label="Locked accounts" value={fmt(abuse.locked)}
                sub={abuse.locked > 0 ? 'locked_until > now' : 'none locked'} subColor={abuse.locked>0?t.warn:t.up}
                accent={abuse.locked > 0 ? '#d97706' : '#17a86a'} onClick={() => onNav && onNav('users')} />
              <KPI t={t} label="Same-day cancels · wk" value={fmt(abuse.sameDayCancelsWeek)}
                sub={abuse.sameDayCancelsWeek > 0 ? 'potential abuse' : 'no abuse pattern'} subColor={abuse.sameDayCancelsWeek>0?t.warn:t.up}
                accent={abuse.sameDayCancelsWeek > 0 ? '#d97706' : '#17a86a'} onClick={() => onNav && onNav('subscriptions')} />
              <KPI t={t} label="Refunds this month" value={fmt(abuse.refundsThisMonth)}
                sub={abuse.refundsThisMonth > 0 ? 'see refund queue' : 'no refunds issued'}
                accent="#9F7E4F" onClick={() => onNav && onNav('refundqueue')} />
            </div>
          </>
        )}

        {/* Feature insights */}
        <SectionLabel t={t}>Feature insights</SectionLabel>
        <div style={{ display:'grid', gridTemplateColumns:col3, gap:12 }}>
          <FeatureCard t={t} title="Sana AI" accent="#5a8fa6" navId="sana" onNav={onNav}
            big={fmt(sana.total)} bigLabel="total AI queries" rows={[
              { label:'Result rate', value:`${sana.resultRate||0}%`, color:(sana.resultRate||0)>70?t.up:t.warn },
              { label:'Today', value:fmt(sana.todayCount||0) },
              { label:'Top query', value:(sana.topQueries||[])[0]?.query || '—', color:t.accent },
            ]} />
          <FeatureCard t={t} title="Saved locations" accent="#819D84" navId="saved" onNav={onNav}
            big={fmt(saved.total)} bigLabel="total saves" rows={[
              { label:'Users saving', value:fmt(saved.uniqueUsers||0) },
              { label:'Unique locations', value:fmt(saved.uniqueLocations||0) },
              { label:'Avg saves/user', value: saved.uniqueUsers ? (saved.total/saved.uniqueUsers).toFixed(1) : '—' },
            ]} />
          <FeatureCard t={t} title="Community" accent="#9F7E4F" navId="chat" onNav={onNav}
            big={fmt(comm.totalProfiles||0)} bigLabel="members" rows={[
              { label:'Channels', value:fmt(comm.totalChannels||0) },
              { label:'Messages', value:fmt(comm.totalMessages||0) },
              { label:'Today', value:fmt(comm.todayMessages||0) },
            ]} />
        </div>

        <div style={{ display:'grid', gridTemplateColumns:col3, gap:12, marginTop:12 }}>
          <FeatureCard t={t} title="Health profiles" accent="#AFE1F6" navId="travelprofiles" onNav={onNav}
            big={fmt(prof.total||0)} bigLabel="users with profile" rows={[
              { label:'Completion rate', value:`${prof.completionRate||0}%`, color:(prof.completionRate||0)>50?t.up:t.mut },
              ...(prof.topConditions||[]).slice(0,2).map(c => ({ label:c.condition, value:fmt(c.count), color:t.accent })),
            ]} />
          <FeatureCard t={t} title="Passes" accent="#314F5C" navId="revenue" navLabel="Revenue" onNav={onNav}
            big={fmt(passes.total)} bigLabel="passes issued" rows={[
              { label:'Active now', value:fmt(passes.active), color:t.up },
              { label:'This week', value:fmt(passes.thisWeek||0) },
              ...(passes.byType||[]).slice(0,2).map(ty => ({ label:(ty.type||'').replace('PASS_',''), value:fmt(ty.count), color:t.accent })),
            ]} />
          <FeatureCard t={t} title="Revenue & MRR" accent="#17a86a" navId="revenue" navLabel="Revenue" onNav={onNav}
            big={hasRevenue ? fmtUSD(rev.totalRev) : '$0'} bigLabel={hasRevenue ? 'all-time net' : 'pre-launch — armed'} rows={hasRevenue ? [
              { label:'Buyers', value:fmt(rev.uniqueUsers) },
              { label:'ARPU', value:`$${rev.arpu}` },
              { label:'Refunds', value:fmt(rev.refunds) },
            ] : [
              { label:'30-day net', value:'$0' },
              { label:'Funnel', value:'awaiting access_event', color:t.mut },
            ]} />
        </div>

        {/* Across the workspace */}
        <SectionLabel t={t} right="manage · marketing · team">Across the workspace</SectionLabel>
        <div style={{ display:'grid', gridTemplateColumns:col3, gap:12 }}>
          <FeatureCard t={t} title="Partners & visas" accent="#6F8FA7" navId="partners" navLabel="Partners" onNav={onNav}
            big={cockpit ? fmt(cm.partners_total||0) : '…'} bigLabel="partner venues" rows={[
              { label:'Pending verification', value: cockpit ? fmt(cm.partners_pending||0) : '…', color:(cm.partners_pending||0)>0?t.warn:t.mut },
              { label:'Visa portals', value: cockpit ? fmt(cm.visa_portals||0) : '…' },
              { label:'Visa countries', value: cockpit ? fmt(cm.visa_countries||0) : '…', color:t.accent },
            ]} />
          <FeatureCard t={t} title="Marketing" accent="#9F7E4F" navId="waitlist" navLabel="Waitlist" onNav={onNav}
            big={cockpit ? fmt(ck.waitlist_total||0) : '…'} bigLabel="waitlist signups" rows={[
              { label:'Pending invites', value: cockpit ? fmt(ck.waitlist_pending||0) : '…', color:(ck.waitlist_pending||0)>0?t.accent:t.mut },
              { label:'Scheduled posts', value: cockpit ? fmt(ck.social_scheduled||0) : '…' },
              { label:'A/B tests running', value: cockpit ? fmt(ck.abtests_running||0) : '…' },
            ]} />
          <FeatureCard t={t} title="Team & ops" accent="#819D84" navId="planner" navLabel="Planner" onNav={onNav}
            big={cockpit ? fmt(ct.tasks_open||0) : '…'} bigLabel="open team tasks" rows={[
              { label:'Active members', value: cockpit ? fmt(ct.team_active||0) : '…' },
              { label:'Upcoming events', value: cockpit ? fmt(ct.events_upcoming||0) : '…' },
              { label:'Open tickets', value: cockpit ? fmt(ct.tickets_open||0) : '…', color:(ct.tickets_open||0)>0?t.warn:t.up },
            ]} />
        </div>

        {/* Recent activity */}
        <SectionLabel t={t} right="realtime">Recent activity</SectionLabel>
        <Panel t={t} title="◷ Activity feed">
          {afL
            ? <div style={{ color:t.mut, fontSize:'1.05rem', padding:'10px 0' }}>Loading activity…</div>
            : feed.length === 0
              ? <div style={{ textAlign:'center', padding:'20px 0' }}>
                  <div style={{ fontSize:'1.1rem', color:t.mut, marginBottom:4 }}>No activity yet</div>
                  <div style={{ fontSize:'0.93rem', color:t.mut }}>Admin actions will appear here in real time</div>
                </div>
              : feed.map(item => <FeedItem key={item.id} t={t} item={item} />)}
        </Panel>

        <CCDrawer t={t} open={!!sel} onClose={() => setSel(null)} title={sel || 'Country'}>
          {sel && <CCCountryInspector t={t} country={sel} />}
          {sel && (
            <div style={{ marginTop:16, display:'flex', gap:8, flexWrap:'wrap' }}>
              <button onClick={() => { setFocus({ type:'country', value:sel }); onNav && onNav('directory'); }} style={{ fontSize:'0.82rem', padding:'7px 12px', borderRadius:7, cursor:'pointer', border:`1px solid ${t.bd}`, background:t.period, color:t.tx, fontWeight:500 }}>Open in Directory →</button>
              <button onClick={() => { setFocus({ type:'sana-country', value:sel }); onNav && onNav('sana'); }} style={{ fontSize:'0.82rem', padding:'7px 12px', borderRadius:7, cursor:'pointer', border:`1px solid ${t.bd}`, background:t.period, color:t.tx, fontWeight:500 }}>Sana activity →</button>
            </div>
          )}
        </CCDrawer>

        <CCDrawer t={t} open={!!selType} onClose={() => setSelType(null)} title={selType || 'Type'}>
          {selType && <CCTypeInspector t={t} type={selType} />}
          {selType && (
            <div style={{ marginTop:16 }}>
              <button onClick={() => { setFocus({ type:'type', value:selType }); onNav && onNav('directory'); }} style={{ fontSize:'0.82rem', padding:'7px 12px', borderRadius:7, cursor:'pointer', border:`1px solid ${t.bd}`, background:t.period, color:t.tx, fontWeight:500 }}>Open in Directory →</button>
            </div>
          )}
        </CCDrawer>

      </div>
    </div>
  );
}
